import React from 'react';
import { Link } from 'react-router-dom';
import LegalPage from './LegalPage';

export default function TermsOfUse() {
  return (
    <LegalPage
      title="Terms of Use"
      description="Terms governing use of the Klean Leaf website, including product information, inquiries and intellectual property."
      path="/terms"
    >
      <p>
        These Terms of Use ("Terms") govern your use of{' '}
        <a href="https://www.kleanleaf.us">www.kleanleaf.us</a> (the "Site"),
        operated by Klean Leaf LLC ("Klean Leaf," "we," "us"). By using the
        Site, you agree to these Terms. If you do not agree, please do not use
        the Site.
      </p>

      <section aria-labelledby="terms-section-1">
        <h2 id="terms-section-1">1. Business Use</h2>
        <p>
          The Site provides information about Klean Leaf's private label
          manufacturing services for businesses. By using the Site or
          submitting an inquiry, you confirm that you are at least 18 years old
          and, if you act for a company, that you are authorized to do so.
        </p>
      </section>

      <section aria-labelledby="terms-section-2">
        <h2 id="terms-section-2">2. Site Information Is Not an Offer</h2>
        <p>
          Product descriptions, formulas, ingredient lists, specifications,
          packaging options, minimum order quantities (MOQs), pricing
          indications, lead times, sample terms and other information on the
          Site are provided for general information only. They may change
          without notice and do not constitute an offer, quotation or guarantee.
        </p>
        <p>
          All orders are governed solely by a written quotation, proforma
          invoice, supply agreement or other written contract signed or
          confirmed by Klean Leaf. If anything on the Site conflicts with such a
          document, that document controls.
        </p>
      </section>

      <section aria-labelledby="terms-section-3">
        <h2 id="terms-section-3">3. Images and Illustrations</h2>
        <p>
          Some images on the Site, including packaging, lifestyle and
          product-use images, are illustrations or renderings created to show
          possible formats and branding. Actual products, packaging, colors and
          dimensions may differ and will be confirmed through samples and
          approved specifications.
        </p>
      </section>

      <section aria-labelledby="terms-section-4">
        <h2 id="terms-section-4">4. Guides and Blog Content</h2>
        <p>
          Articles, guides and checklists on the Site, including content about
          labeling, packaging or regulatory topics, are general information
          only and are not legal, regulatory or professional advice.
          Requirements vary by product, market and sales channel. You are
          responsible for confirming the requirements that apply to your
          products, brand claims and labels with qualified advisors.
        </p>
      </section>

      <section aria-labelledby="terms-section-5">
        <h2 id="terms-section-5">5. Your Brand and Product Claims</h2>
        <p>
          If you work with us on a private label product, you are responsible
          for your brand name, trademarks, artwork, marketing claims and
          compliance with the laws of the markets where you sell, except as
          otherwise agreed in writing. Our review of artwork, labels or claims
          does not replace your own legal and regulatory review.
        </p>
      </section>

      <section aria-labelledby="terms-section-6">
        <h2 id="terms-section-6">6. Intellectual Property</h2>
        <p>
          The Site and its content, including text, images, graphics, logos and
          the Klean Leaf name, are owned by or licensed to Klean Leaf and
          protected by intellectual property laws. You may view and print pages
          for your own business evaluation. You may not copy, modify,
          distribute or use Site content for commercial purposes without our
          written permission.
        </p>
        <p>
          Your brand names, logos and artwork remain your property. By sending
          them to us, you grant us a limited right to use them only to prepare
          quotes, samples, dielines, artwork proofs and products for you.
        </p>
      </section>

      <section aria-labelledby="terms-section-7">
        <h2 id="terms-section-7">7. Inquiries and Project Information</h2>
        <p>
          When you submit an inquiry or project information, you confirm that
          it is accurate and that you have the right to share it. We treat
          non-public project information you share with us, such as formulas,
          artwork and launch plans, as confidential, and use it only to
          evaluate and serve your project.
        </p>
        <p>
          Our handling of personal information is described in our{' '}
          <Link to="/privacy-policy">Privacy Policy</Link>.
        </p>
      </section>

      <section aria-labelledby="terms-section-8">
        <h2 id="terms-section-8">8. Acceptable Use</h2>
        <p>You agree not to:</p>
        <ul>
          <li>use the Site for any unlawful purpose;</li>
          <li>submit false, misleading, spam or automated inquiries;</li>
          <li>
            attempt to gain unauthorized access to the Site or its systems, or
            interfere with its operation;
          </li>
          <li>
            copy or scrape Site content using automated means without our
            permission; or
          </li>
          <li>upload or transmit viruses or other harmful code.</li>
        </ul>
      </section>

      <section aria-labelledby="terms-section-9">
        <h2 id="terms-section-9">9. Third-Party Links</h2>
        <p>
          The Site may link to third-party websites and services, such as social
          media platforms. We are not responsible for their content, policies
          or practices.
        </p>
      </section>

      <section aria-labelledby="terms-section-10">
        <h2 id="terms-section-10">10. Disclaimer</h2>
        <p>
          THE SITE AND ITS CONTENT ARE PROVIDED "AS IS" AND "AS AVAILABLE,"
          WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING
          WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
          ACCURACY AND NON-INFRINGEMENT. Warranties for products we manufacture,
          if any, are set out only in the applicable written contract.
        </p>
      </section>

      <section aria-labelledby="terms-section-11">
        <h2 id="terms-section-11">11. Limitation of Liability</h2>
        <p>
          TO THE FULLEST EXTENT PERMITTED BY LAW, KLEAN LEAF WILL NOT BE LIABLE
          FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL OR PUNITIVE
          DAMAGES, OR FOR ANY LOSS OF PROFITS, REVENUE OR DATA, ARISING FROM YOUR
          USE OF OR RELIANCE ON THE SITE. OUR TOTAL LIABILITY FOR ANY CLAIM
          ARISING FROM THE SITE WILL NOT EXCEED ONE HUNDRED U.S. DOLLARS
          (US$100). This section does not limit liability under any written
          contract for products or services, which is governed by that contract.
        </p>
      </section>

      <section aria-labelledby="terms-section-12">
        <h2 id="terms-section-12">12. Indemnity</h2>
        <p>
          You agree to indemnify and hold harmless Klean Leaf from claims,
          losses and expenses, including reasonable attorneys' fees, arising
          from your breach of these Terms or your misuse of the Site.
        </p>
      </section>

      <section aria-labelledby="terms-section-13">
        <h2 id="terms-section-13">13. Governing Law</h2>
        <p>
          These Terms are governed by the laws of the State of Ohio, USA,
          without regard to its conflict-of-law rules. Any dispute arising from
          these Terms or the Site will be brought exclusively in the state or
          federal courts located in Franklin County, Ohio, and you consent to
          their jurisdiction.
        </p>
      </section>

      <section aria-labelledby="terms-section-14">
        <h2 id="terms-section-14">14. Changes</h2>
        <p>
          We may update these Terms at any time by posting the revised version
          on this page and changing the "Last updated" date. Your continued use
          of the Site after changes are posted means you accept the updated
          Terms.
        </p>
      </section>

      <section aria-labelledby="terms-section-15">
        <h2 id="terms-section-15">15. Contact</h2>
        <p>
          Questions about these Terms:{' '}
          <a href="mailto:hello@kleanleaf.us">hello@kleanleaf.us</a>, Klean Leaf LLC,
          Columbus, Ohio, United States.
        </p>
      </section>
    </LegalPage>
  );
}
